import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router';
import { Box, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import './breadcrumbs.css';
import { useDispatch, useSelector } from 'react-redux';
import { setFilters, setPreservedFilters, clearNonBreadcrumbFilters } from 'src/redux/reducers/filtersReducer';
import { breadcrumbMappings } from './breadcrumbMappings';

const TIMEOUT_DURATION = 480 * 60 * 1000; // 480 minutes in milliseconds

export default function BreadCrumbs({ sx, action, heading, moreLink, activeLast, filters }) {
  const location = useLocation();
  const dispatch = useDispatch();
  const preservedFilters = useSelector((state) => state.filters.preservedFilters);

  const [currentPath, setCurrentPath] = useState('');
  const [previousPath, setPreviousPath] = useState('');

  // Keep existing path management
  useEffect(() => {
    const storedPreviousPath = localStorage.getItem('previousPath');
    const storedCurrentPath = localStorage.getItem('currentPath');
    const storedTimestamp = localStorage.getItem('lastVisitTimestamp');
    const currPath = location.pathname;
    const currentTime = new Date().getTime();

    if (!storedTimestamp || currentTime - parseInt(storedTimestamp, 10) > TIMEOUT_DURATION) {
      setPreviousPath('');
      setCurrentPath(currPath);
      localStorage.setItem('previousPath', '');
      localStorage.setItem('currentPath', currPath);
    } else if (currPath === storedCurrentPath) {
      setPreviousPath(storedPreviousPath || '');
      setCurrentPath(currPath);
    } else {
      setPreviousPath(storedCurrentPath || '');
      setCurrentPath(currPath);
      localStorage.setItem('previousPath', storedCurrentPath || '');
      localStorage.setItem('currentPath', currPath);
    }

    localStorage.setItem('lastVisitTimestamp', currentTime.toString());
  }, [location.pathname]);

  // Handle filter preservation - only update when filters prop changes
  useEffect(() => {
    if (filters && Object.keys(filters).length > 0) {
      dispatch(setPreservedFilters({ path: location.pathname, filters }));
    }
  }, [filters, location.pathname, dispatch]);

  // Clean up filters for paths no longer in breadcrumbs
  useEffect(() => {
    const storedPreviousPath = localStorage.getItem('previousPath');
    const storedCurrentPath = localStorage.getItem('currentPath');
    const activePaths = [storedPreviousPath, storedCurrentPath].filter(Boolean);
    dispatch(clearNonBreadcrumbFilters({ activePaths }));
  }, [dispatch, previousPath, currentPath]);

  // Load preserved filters for current path
  useEffect(() => {
    const currentPathFilters = preservedFilters[location.pathname];
    if (currentPathFilters) {
      dispatch(setFilters(currentPathFilters));
    }
  }, [dispatch, location.pathname, preservedFilters]);

  // Your existing breadcrumb rendering code
  const separatorStyle = {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    backgroundColor: '#696969',
    marginLeft: '12px',
    verticalAlign: 'middle',
  };

  const separator = <span style={{ ...separatorStyle, display: 'inline-block' }}>&nbsp;</span>;

  const getBasePath = (path) => {
    const parts = path.split('/');
    const baseParts = parts.filter((part) => breadcrumbMappings[part]);
    return baseParts.join('/');
  };

  const renderBreadcrumb = (path) => {
    const basePath = getBasePath(path);
    const parts = basePath.split('/');
    const lastPart = parts[parts.length - 1];
    const mappedName = breadcrumbMappings[lastPart];
    return mappedName || lastPart;
  };

  const breadcrumbs = [
    ['Dashboard', '/dashboard'],
    [renderBreadcrumb(previousPath), previousPath],
    [renderBreadcrumb(currentPath), currentPath],
  ]
    .filter(([, path]) => path)
    .map(([displayName, path], index, array) => {
      const isLastCrumb = index === array.length - 1;

      return (
        <div className="crumb" key={`${path}-${index}`}>
          {isLastCrumb ? (
            <span>{displayName}</span>
          ) : (
            <>
              <Link to={path} style={{ textDecoration: 'underline', color: 'inherit' }}>
                {displayName}
              </Link>
              {separator}
            </>
          )}
        </div>
      );
    });

  return (
    <Box sx={{ mt: 2, ...sx }}>
      <Stack direction="row" alignItems="center">
        <Box sx={{ flexGrow: 1 }}>
          {heading && (
            <Typography variant="h4" gutterBottom>
              {heading}
            </Typography>
          )}

          {!!breadcrumbs.length && <div className="breadcrumbs">{breadcrumbs}</div>}
        </Box>

        {action && <Box sx={{ flexShrink: 0 }}> {action} </Box>}
      </Stack>

      {!!moreLink && (
        <Box sx={{ mt: 2 }}>
          {moreLink.map((href, index) => (
            <Link
              key={index}
              to={href}
              variant="body2"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'table' }}
            >
              {href}
            </Link>
          ))}
        </Box>
      )}
    </Box>
  );
}

BreadCrumbs.propTypes = {
  sx: PropTypes.object,
  action: PropTypes.node,
  heading: PropTypes.string,
  moreLink: PropTypes.array,
  activeLast: PropTypes.bool,
  filters: PropTypes.object,
};
